import type { SeaTurtles, TurtleState, World } from '../types';
import type { SiteId } from '../data/sites';
import { HONU, HONUEA, stepHonu, type TurtleLife, type TurtleSupply } from '../turtles';
import { HONU_JANUARY, HONU_SUPPLY } from '../data/turtles/honu';
import { HONUEA_JANUARY, HONUEA_SUPPLY } from '../data/turtles/honuea';
import { STREAM, stockStream } from '../streams';
import { EXPECTED_DRAWS, stockDraws, type PopulationDraws } from '../population';
import { civilFromEpochDay, epochDayFromCivil, epochDayOf } from '../calendar';

/** A turtle's draws on its stock's stream of the day (`stockDraws`, one stock: Maui Nui's): binomial shares of each class's count, a Poisson draw of the hatchlings; the honu's unless another is named (the hawksbill's, M187). */
export function turtleDraws(world: { seed: number; day: number }, siteId: SiteId, streamOf: typeof stockStream = stockStream, salt: number = STREAM.honu): PopulationDraws {
  return stockDraws(world, salt, siteId, streamOf);
}

/** Each turtle the game steps, in a sanctuary's order: its life, its balance and its stream (the hawksbill since M187). */
export const TURTLES: readonly { key: 'honu' | 'honuea'; life: TurtleLife; supply: TurtleSupply; january: { atSea: readonly number[]; home: readonly number[] }; salt: number }[] = [
  { key: 'honu', life: HONU, supply: HONU_SUPPLY, january: HONU_JANUARY, salt: STREAM.honu },
  { key: 'honuea', life: HONUEA, supply: HONUEA_SUPPLY, january: HONUEA_JANUARY, salt: STREAM.honuea },
];

/**
 * Step 3 of the tick, the animals, after the reef fish (M93): each
 * sanctuary's honu, and since M187 its hawksbills after them, die, recruit, leave to breed and come home, and the
 * season's hatchlings reach the sea (`turtles.ts`), on the honu's own stream
 * of the day (a test hands in its own as `streamOf`).
 */
export function stepTurtles(world: World, streamOf: typeof stockStream = stockStream): void {
  const epochDay = epochDayOf(world);
  for (const s of world.sanctuaries) {
    const turtles = s.turtles;
    if (turtles) for (const t of TURTLES) stepHonu(t.life, turtles[t.key], epochDay, t.supply, turtleDraws(world, s.siteId, streamOf, t.salt));
  }
}

/** Whether a site has honu: Maui Nui only, the share of the archipelago's the towed divers' counts give (`MAUI_NUI_SHARE`). */
export const siteHasTurtles = (siteId: SiteId): boolean => siteId === 'maui_nui';

/**
 * A sanctuary's honu as a world opens on a day: the balance's first of
 * January (`data/turtles/`), stepped at its expected values through the
 * opening year's days to the opening day, each class's count then rounded
 * to a whole turtle (no draw: a world's opening draws nothing).
 */
export function openingTurtles(epochDay: number): SeaTurtles {
  const year = civilFromEpochDay(epochDay).year;
  const opened = (t: (typeof TURTLES)[number]): TurtleState => {
    const none = (): number[] => t.january.atSea.map(() => 0);
    const x: TurtleState = { atSea: [...t.january.atSea], home: [...t.january.home], awayFemales: none(), awayMales: none(), hatchlings: 0 };
    for (let d = epochDayFromCivil(year, 1, 1); d <= epochDay; d++) stepHonu(t.life, x, d, t.supply, EXPECTED_DRAWS);
    const whole = (xs: number[]): number[] => xs.map((n) => Math.round(n));
    return { atSea: whole(x.atSea), home: whole(x.home), awayFemales: whole(x.awayFemales), awayMales: whole(x.awayMales), hatchlings: x.hatchlings };
  };
  return { honu: opened(TURTLES[0] as (typeof TURTLES)[number]), honuea: opened(TURTLES[1] as (typeof TURTLES)[number]) };
}

/** A sanctuary's honu as a world opens on a day, where its site has them (`siteHasTurtles`); elsewhere none. */
export function sanctuaryOpeningTurtles(siteId: SiteId, epochDay: number): SeaTurtles | null {
  return siteHasTurtles(siteId) ? openingTurtles(epochDay) : null;
}
